/* tslint:disable:max-line-length */
import {MmProjectDbCollections} from '../../mm-db/mm-project-db-schema';
import {Injectable, NgZone} from '@angular/core';
import {MmDbService} from '../../mm-db/mm-db-service';
import { ChangeState } from '@docma-mm-core/model/change-state/change-state';
import { ScribbleData } from './scribble';
import {getAttchmantFromEntity} from '@docma-mm-core/attachment/attachment-db.service';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {AttachmentData} from '@docma-mm-core/attachment/attachment';
import {Logger} from '@mydocma/common/src/lib/logger';

@Injectable()
export class ScribbleDbService {

  constructor(private readonly mmDbService: MmDbService,
              private ngZone: NgZone,
              private readonly logger: Logger) {
  }

  async saveChangeState(mydocmaUid: string, faultUid: string, activityUId: string, groupUid: string, attachmentUid: string, scribbleUid: string, changeState: ChangeState<ScribbleData>): Promise<ChangeState<ScribbleData>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (!entity) {
        throw new Error(`An error while persisting fault change state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
      }

      try {
        const attachmentEntity = getAttchmantFromEntity(entity, activityUId, groupUid, attachmentUid);
        if (!attachmentEntity) {
          throw new Error(`Attachment uid ${faultUid}/${attachmentUid} not found in project store ${mydocmaUid}`);
        }

        const scribbleEntity = attachmentEntity.scribbles.find(sc => scribbleUid && !!isNaN(+scribbleUid) && sc.uid === scribbleUid);
        if (!scribbleEntity) {
          throw new Error(`Scribble uid ${faultUid}/${attachmentUid}/${scribbleUid} not found in project store ${mydocmaUid}`);
        }

        scribbleEntity.changeState = changeState;
      } catch (err) {
        throw new Error(`An error while persisting scribble change state in project ${mydocmaUid}.` + err.message);
      }
      await db.set(MmProjectDbCollections.faults as never, entity);
      return new ChangeState<ScribbleData>(changeState);
    });
  }

  async saveLoadStates(mydocmaUid: string, faultUid: string, activityUId: string, groupUid: string, attachmentUid: string, loadStates: LoadState<ScribbleData>[], isDeleted: boolean, throwErrorIfNotFound?: boolean): Promise<LoadState<ScribbleData>[] | null> {
    return await this.ngZone.runOutsideAngular(async () => {
      const result: LoadState<ScribbleData>[] = [];
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (!entity) {
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting scribble load state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
        } else {
          this.logger.error(`An error while persisting scribble load state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`, '', 'ScribbleDbService');
        }
        return null;
      }

      try {
        const attachmentEntity = getAttchmantFromEntity(entity, activityUId, groupUid, attachmentUid);
        if (!attachmentEntity) {
          throw new Error(`Attachment uid ${faultUid}/${attachmentUid} not found in project store ${mydocmaUid}`);
        }

        for (const loadState of loadStates) {
          const scribbleUid = (loadState.data as ScribbleData).UniqueId;

          const scribbleEntityIndex = attachmentEntity.scribbles.findIndex(sc => scribbleUid && !!isNaN(+scribbleUid) && sc.uid === scribbleUid);
          if (scribbleEntityIndex === -1) {
            throw new Error(`Scribble uid ${faultUid}/${attachmentUid}/${scribbleUid} not found in project store ${mydocmaUid}`);
          }

          if (!isDeleted) {
            const scribbleEntity = attachmentEntity.scribbles[scribbleEntityIndex];
            scribbleEntity.loadState = loadState;
            scribbleEntity.id = loadState.data?.Id ?? scribbleEntity.id;
          } else {
            attachmentEntity.scribbles.splice(scribbleEntityIndex, 1);
          }

          result.push(new LoadState<ScribbleData>(loadState));
        }
      } catch (err) {
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting scribble load state in project ${mydocmaUid}.` + err.message);
        } else {
          this.logger.warn(`An error while persisting scribble load state in project ${mydocmaUid}.` + err.message, 'ScribbleDbService');
        }
        return null;
      }
      await db.set(MmProjectDbCollections.faults as never, entity);
      return result;
    });
  }

  async saveSyncState(mydocmaUid: string, faultUid: string, activityUId: string, groupUid: string, attachmentUid: string, scribbleUids: string[], state: SyncState, throwErrorIfNotFound?: boolean) {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (entity) {
        try {
          const attachmentEntity = getAttchmantFromEntity(entity, activityUId, groupUid, attachmentUid);
          if (!attachmentEntity) {
            throw new Error(`Attachment uid ${faultUid}/${attachmentUid} not found in project store ${mydocmaUid}`);
          }

          for (const scribbleUid of scribbleUids) {
            const scribbleEntity = attachmentEntity.scribbles.find(sc => scribbleUid && !!isNaN(+scribbleUid) && sc.uid === scribbleUid);
            if (!scribbleEntity) {
              throw new Error(`Scribble uid ${faultUid}/${attachmentUid}/${scribbleUid} not found in project store ${mydocmaUid}`);
            }

            scribbleEntity.syncState = {...state};
            delete scribbleEntity.syncState.changes;
          }
          await db.set(MmProjectDbCollections.faults as never, entity);

        } catch (err) {
          // TODO: Log error
          if (throwErrorIfNotFound) {
            throw new Error(`An error while persisting scribble sync state in project ${mydocmaUid}.` + err.message);
          } else {
            this.logger.warn(`An error while persisting scribble sync state in project ${mydocmaUid}.` + err.message, 'ScribbleDbService');
          }
        }
      }
      return state;
    });
  }


}
